import type { TableState, TableAction, SortState } from "./types";

export function createInitialState(config: {
  defaultPageSize: number;
  defaultSort?: SortState | null;
  initialData?: unknown[];
  defaultExpandedGroups?: boolean;
}): TableState {
  return {
    pagination: { pageIndex: 0, pageSize: config.defaultPageSize },
    sort: config.defaultSort ?? null,
    searchText: "",
    filters: {},
    selectedKeys: [],
    editingRows: new Map(),
    editErrors: new Map(),
    serverErrors: new Map(),
    data: config.initialData ?? [],
    originalData: config.initialData ?? [],
    expandedRows: new Set(),
    expandedGroups: new Set(),
    columnOrder: [],
    columnSizes: {},
  };
}

export function tableStateReducer(
  state: TableState,
  action: TableAction,
): TableState {
  switch (action.type) {
    case "SET_DATA":
      // REGRESSION GUARD — always reset transient UI state when data changes.
      // Row keys in the new dataset may collide with old keys, causing React
      // to reuse DOM nodes with stale expansion/edit/selection state.
      return {
        ...state,
        data: action.data,
        originalData: action.data,
        selectedKeys: [],
        expandedRows: new Set(),
        editingRows: new Map(),
        editErrors: new Map(),
        serverErrors: new Map(),
      };

    case "ACCUMULATE_DATA":
      return {
        ...state,
        data: action.data,
        originalData: action.data,
      };

    case "SET_PAGE":
      return {
        ...state,
        pagination: { ...state.pagination, pageIndex: action.pageIndex },
      };

    case "SET_PAGE_SIZE":
      return {
        ...state,
        pagination: { pageIndex: 0, pageSize: action.pageSize },
      };

    case "SET_SORT": {
      // REGRESSION GUARD — reset transient UI state on sort change.
      // Sort changes data order, making index-based row keys point at
      // different logical rows. Selection/expansion must not carry over.
      const sortReset = {
        selectedKeys: [] as string[],
        expandedRows: new Set<string>(),
        editingRows: new Map<string, Record<string, unknown>>(),
        editErrors: new Map<string, Record<string, string>>(),
        serverErrors: new Map<string, Record<string, string>>(),
      };
      if (state.sort?.field === action.field) {
        if (state.sort.direction === "asc") {
          return {
            ...state,
            ...sortReset,
            sort: { field: action.field, direction: "desc" },
            pagination: { ...state.pagination, pageIndex: 0 },
          };
        }
        return {
          ...state,
          ...sortReset,
          sort: null,
          pagination: { ...state.pagination, pageIndex: 0 },
        };
      }
      return {
        ...state,
        ...sortReset,
        sort: { field: action.field, direction: "asc" },
        pagination: { ...state.pagination, pageIndex: 0 },
      };
    }

    case "CLEAR_SORT":
      return {
        ...state,
        sort: null,
        selectedKeys: [],
        expandedRows: new Set(),
      };

    case "SET_SEARCH":
      return {
        ...state,
        searchText: action.text,
        pagination: { ...state.pagination, pageIndex: 0 },
        selectedKeys: [],
        expandedRows: new Set(),
      };

    case "SET_FILTER":
      return {
        ...state,
        filters: { ...state.filters, [action.name]: action.value },
        pagination: { ...state.pagination, pageIndex: 0 },
        selectedKeys: [],
        expandedRows: new Set(),
      };

    case "SET_FILTERS":
      return {
        ...state,
        filters: action.filters,
        pagination: { ...state.pagination, pageIndex: 0 },
        selectedKeys: [],
        expandedRows: new Set(),
      };

    case "CLEAR_FILTERS":
      return {
        ...state,
        filters: {},
        pagination: { ...state.pagination, pageIndex: 0 },
        selectedKeys: [],
        expandedRows: new Set(),
      };

    case "SELECT_ROW":
      return { ...state, selectedKeys: [...state.selectedKeys, action.key] };

    case "DESELECT_ROW":
      return {
        ...state,
        selectedKeys: state.selectedKeys.filter((k) => k !== action.key),
      };

    case "SELECT_ALL":
      return { ...state, selectedKeys: action.keys };

    case "DESELECT_ALL":
      return { ...state, selectedKeys: [] };

    case "TOGGLE_ROW": {
      const isSelected = state.selectedKeys.includes(action.key);
      return {
        ...state,
        selectedKeys: isSelected
          ? state.selectedKeys.filter((k) => k !== action.key)
          : [...state.selectedKeys, action.key],
      };
    }

    case "START_EDITING": {
      const newMap = new Map(state.editingRows);
      newMap.set(action.rowKey, action.initialValues);
      return { ...state, editingRows: newMap };
    }

    case "START_EDITING_BATCH": {
      const newMap = new Map(state.editingRows);
      for (const entry of action.entries) {
        newMap.set(entry.rowKey, entry.initialValues);
      }
      return { ...state, editingRows: newMap };
    }

    case "UPDATE_EDIT_VALUE": {
      const newMap = new Map(state.editingRows);
      const existing = newMap.get(action.rowKey) ?? {};
      newMap.set(action.rowKey, { ...existing, [action.field]: action.value });
      return { ...state, editingRows: newMap };
    }

    case "PATCH_EDIT_ROW": {
      const newMap = new Map(state.editingRows);
      const existing = newMap.get(action.rowKey) ?? {};
      newMap.set(action.rowKey, { ...existing, ...action.values });
      return { ...state, editingRows: newMap };
    }

    case "CANCEL_EDITING": {
      const newMap = new Map(state.editingRows);
      newMap.delete(action.rowKey);
      const newErrors = new Map(state.editErrors);
      newErrors.delete(action.rowKey);
      const newServerErrors = new Map(state.serverErrors);
      newServerErrors.delete(action.rowKey);
      return { ...state, editingRows: newMap, editErrors: newErrors, serverErrors: newServerErrors };
    }

    case "CANCEL_EDITING_BATCH": {
      const newMap = new Map(state.editingRows);
      const newErrors = new Map(state.editErrors);
      const newServerErrors = new Map(state.serverErrors);
      for (const key of action.rowKeys) {
        newMap.delete(key);
        newErrors.delete(key);
        newServerErrors.delete(key);
      }
      return { ...state, editingRows: newMap, editErrors: newErrors, serverErrors: newServerErrors };
    }

    case "CANCEL_ALL_EDITING":
      return { ...state, editingRows: new Map(), editErrors: new Map(), serverErrors: new Map() };

    case "SET_EDIT_ERROR": {
      const newErrors = new Map(state.editErrors);
      const rowErrors = {
        ...(newErrors.get(action.rowKey) ?? {}),
        [action.field]: action.error,
      };
      newErrors.set(action.rowKey, rowErrors);
      return { ...state, editErrors: newErrors };
    }

    case "CLEAR_EDIT_ERROR": {
      const newErrors = new Map(state.editErrors);
      const rowErrors = { ...(newErrors.get(action.rowKey) ?? {}) };
      delete rowErrors[action.field];
      if (Object.keys(rowErrors).length === 0) {
        newErrors.delete(action.rowKey);
      } else {
        newErrors.set(action.rowKey, rowErrors);
      }
      return { ...state, editErrors: newErrors };
    }

    case "SET_ROW_EDIT_ERRORS": {
      const newErrors = new Map(state.editErrors);
      if (Object.keys(action.errors).length === 0) {
        newErrors.delete(action.rowKey);
      } else {
        newErrors.set(action.rowKey, action.errors);
      }
      return { ...state, editErrors: newErrors };
    }

    case "SET_SERVER_ERRORS":
      return { ...state, serverErrors: action.errors };

    case "CLEAR_SERVER_ERRORS":
      return { ...state, serverErrors: new Map() };

    case "APPLY_EDIT": {
      const editedValues = state.editingRows.get(action.rowKey);
      if (!editedValues) return state;
      const newData = state.data.map((row) => {
        const rec = row as Record<string, unknown>;
        const rowKey = String(rec.id ?? "");
        if (rowKey === action.rowKey) {
          return { ...rec, ...editedValues };
        }
        return row;
      });
      const newEditing = new Map(state.editingRows);
      newEditing.delete(action.rowKey);
      return { ...state, data: newData, editingRows: newEditing };
    }

    case "TOGGLE_EXPAND": {
      const newExpanded = new Set(state.expandedRows);
      if (newExpanded.has(action.rowKey)) {
        newExpanded.delete(action.rowKey);
      } else {
        newExpanded.add(action.rowKey);
      }
      return { ...state, expandedRows: newExpanded };
    }

    case "EXPAND_ALL":
      return { ...state, expandedRows: new Set(action.keys) };

    case "COLLAPSE_ALL":
      return { ...state, expandedRows: new Set() };

    case "TOGGLE_GROUP": {
      const newExpandedGroups = new Set(state.expandedGroups);
      if (newExpandedGroups.has(action.groupKey)) {
        newExpandedGroups.delete(action.groupKey);
      } else {
        newExpandedGroups.add(action.groupKey);
      }
      return { ...state, expandedGroups: newExpandedGroups };
    }

    case "EXPAND_ALL_GROUPS":
      return { ...state, expandedGroups: new Set(action.keys) };

    case "COLLAPSE_ALL_GROUPS":
      return { ...state, expandedGroups: new Set() };

    case "REORDER_COLUMN": {
      const order = [...state.columnOrder];
      const [moved] = order.splice(action.fromIndex, 1);
      order.splice(action.toIndex, 0, moved);
      return { ...state, columnOrder: order };
    }

    case "SET_COLUMN_ORDER":
      return { ...state, columnOrder: action.order };

    case "RESET_COLUMN_ORDER":
      return { ...state, columnOrder: [] };

    case "SET_COLUMN_SIZES":
      return { ...state, columnSizes: action.sizes };

    case "RESIZE_COLUMN":
      return {
        ...state,
        columnSizes: { ...state.columnSizes, [action.field]: action.width },
      };

    case "RESET_COLUMN_SIZES":
      return { ...state, columnSizes: {} };

    default:
      return state;
  }
}
